import polyline from 'google-polyline';
import { uniqWith } from 'lodash';

import { AIRPORT, CANAL_TRANSIT, MAP_PATH_STYLE, SEAPORT, TRANSFER_TRANSFER, VEHICLE } from '@/lib/constants';
import { LocationModel } from '@/lib/models/location/types';
import { VesselLocationModel } from '@/lib/models/vessel-location/types';
import { MapMarker, MapPath, MapPathStyle, MarkerSize, Paths, PositionInterface } from '@/types';
import { NodeType, PlaceType, PortApiInterface, RouteTransferApiType } from '@/types/api-types';
import { formatDateForUser } from '@/utils/format';
import { getPreferredIdentifier } from '@/utils/helpers';

export const createPathId = (a: string, b: string, prefix: string | number = 'P'): string => {
  return `${prefix}_${a}_${b}`;
};

/**
 * Decodes a path encoded in the Encoded Polyline Algorithm Format
 */
export const decodePolylinePath = (encodedPath?: string): PositionInterface[] | null => {
  if (encodedPath) {
    const decodedPath = polyline.decode(encodedPath);
    const objectPath = decodedPath.map((pos) => {
      const [lat, lng] = pos;
      return {
        lat,
        lng,
      };
    });
    return objectPath;
  }

  return null;
};

/**
 * Creates a Straight Line from a list of node Ids
 * Used for Air results or when Ocean paths fail
 */
export const createPathsFromLocations = (
  locations: LocationModel[] | MapMarker[],
  pathHash?: number | string,
): Paths[] => {
  if (!locations) return [];

  return locations.reduce((pathAccum, location, index) => {
    const nextLocation = locations[index + 1];
    if (nextLocation) {
      const path: Paths = {
        id: createPathId(location.id, nextLocation.id, pathHash),
        positions: [location.location, nextLocation.location],
      };
      return [...pathAccum, path];
    }
    return pathAccum;
  }, [] as Paths[]);
};

/**
 * Gets a specific Map marker depending on the Type and TransferType
 */
export const getMapMarkerIcon = (type: NodeType | PlaceType, transferType?: RouteTransferApiType | null): string => {
  switch (type) {
    case AIRPORT:
      return 'plane';
    case SEAPORT:
      // Switch with in a switch
      switch (transferType) {
        case TRANSFER_TRANSFER:
          return 'split';
        case CANAL_TRANSIT:
          return 'waves';
        default:
          return 'anchor';
      }
    default:
      return '';
  }
};

/**
 * Converts raw decoded paths into a MapPath so we can pass it to the Map
 */
export const pathsToMapPaths = (rawPaths: Paths[] | undefined, style: MapPathStyle = {}): MapPath[] => {
  if (!rawPaths) return [];
  return rawPaths.map((path) => {
    return {
      ...path,
      style: {
        ...MAP_PATH_STYLE,
        ...style,
      },
    };
  });
};

export const createMarkersFromPorts = (ports: PortApiInterface[], completedPorts: string[]): MapMarker[] => {
  return ports.map((port, index) => {
    const isFirst = index === 0;
    const isLast = index === ports.length - 1;
    const isFirstOrLast = isFirst || isLast;
    const isAirportOrSeaport = port.type === AIRPORT || port.type === SEAPORT;
    const identifier = getPreferredIdentifier(port.ids);
    const label = isAirportOrSeaport ? identifier?.value : port.name;
    const isCompleted = identifier?.value ? completedPorts.includes(identifier?.value) : false;
    const markerColor = 'white'; // isCompleted ? 'white' : getTransportColor(port.type); // 'white'; // isAirportOrSeaport ? getTransportColor(port.type) : 'lightBlue.400';
    const size = isFirstOrLast ? 'xl' : 'lg';
    const icon = isCompleted ? 'milestone-complete' : 'milestone-pending';

    return {
      id: `${identifier?.type} ${identifier?.value}`,
      type: 'tracking',
      color: markerColor,
      location: port.location,
      label: label || '',
      size,
      active: false,
      icon,
      nodeType: port.type,
      zIndex: isFirstOrLast ? ports.length + 2 : index + 1,
    };
  });
};

export const createMarkersFromVehicleLocations = (locations: VesselLocationModel[], isActive: boolean): MapMarker[] => {
  const sortedLocations = locations.sort((a, b) => {
    return new Date(a.pingedAt).getTime() - new Date(b.pingedAt).getTime();
  });

  const dedupedLocations = uniqWith(
    sortedLocations,
    (locationA, locationB) => locationA.longitude === locationB.longitude && locationA.latitude === locationB.latitude,
  );

  return dedupedLocations.map((location, index) => {
    const isActiveMarker = isActive && index === dedupedLocations.length - 1;

    return {
      id: location.id,
      type: 'tracking',
      color: isActiveMarker ? '' : 'lightBlue.400',
      location: { lat: location.latitude, lng: location.longitude },
      label: isActiveMarker ? 'Current location' : formatDateForUser(location.pingedAt),
      size: (isActiveMarker ? '2xl' : 'sm') as MarkerSize,
      active: isActiveMarker,
      icon: isActiveMarker ? 'vehicle-location' : '',
      nodeType: VEHICLE,
      zIndex: 1,
    };
  });
};

export const createPathFromVehicleLocations = (locations: VesselLocationModel[]): Paths[] => {
  if (!locations) return [];

  // @TODO consider encoding path for smaller payload size
  return locations.reduce((pathAccum, location, index) => {
    const nextLocation = locations[index + 1];
    if (nextLocation) {
      const locationA = { lat: location.latitude, lng: location.longitude };
      const locationB = { lat: nextLocation.latitude, lng: nextLocation.longitude };
      const path: Paths = {
        id: createPathId(location.id, nextLocation.id),
        positions: [locationA, locationB],
      };
      return [...pathAccum, path];
    }
    return pathAccum;
  }, [] as Paths[]);
};
